/**
 * Map a Cognito SDK error to a user-friendly message.
 *
 * The amazon-cognito-identity-js SDK sets the error code on `err.code`
 * (a string like "NotAuthorizedException"). Some versions also set `err.name`.
 *
 * @param {Error} err — the error thrown by amazon-cognito-identity-js
 * @returns {{ message: string, redirectToForgotPassword?: boolean }}
 */
export function mapCognitoError(err) {
  const code = err.code || err.name || '';

  switch (code) {
    case 'NotAuthorizedException':
      return { message: 'Incorrect password. Please try again.' };

    case 'UserNotFoundException':
      return { message: 'No account found with this email.' };

    case 'UserNotConfirmedException':
      return { message: 'Your account has not been verified.' };

    case 'PasswordResetRequiredException':
      return {
        message: 'You must reset your password before signing in.',
        redirectToForgotPassword: true,
      };

    case 'TooManyRequestsException':
      return { message: 'Too many attempts. Please wait and try again.' };

    case 'LimitExceededException':
      return { message: 'Too many attempts. Please wait a few minutes.' };

    case 'ExpiredCodeException':
      return { message: 'This code has expired. Request a new one.' };

    case 'CodeMismatchException':
      return { message: 'Invalid code. Please check and try again.' };

    case 'InvalidPasswordException':
      return { message: 'Password doesn\'t meet requirements.' };

    case 'InvalidParameterException': {
      // Cognito sometimes returns verbose messages like
      // "2 validation errors detected: Value at 'password' failed to satisfy..."
      const msg = err.message || '';
      if (/validation error/i.test(msg)) {
        return { message: 'Password doesn\'t meet requirements.' };
      }
      return { message: msg || 'Invalid input. Please check and try again.' };
    }

    default:
      return { message: err.message || 'Something went wrong. Please try again.' };
  }
}
